import React, { useState } from 'react';
import moment from 'moment';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';
import { MOCK_SALES, getSalesTotals, SaleType, PaymentStatus, Sale } from '../mocks/sales.mock';

moment.locale('it');

type FilterType = 'all' | SaleType;

const statPill = (label: string, value: string | number) => (
    <div
        className='d-flex align-items-center gap-2'
        style={{
            padding: '6px 14px',
            borderRadius: 50,
            backgroundColor: '#f5f5f5',
            fontSize: '0.78rem',
            fontWeight: 500,
            color: '#555',
            whiteSpace: 'nowrap',
        }}
    >
        <span style={{ color: '#999' }}>{label}</span>
        <span style={{ fontWeight: 700, color: '#333' }}>{value}</span>
    </div>
);

export const SalesView: React.FC = () => {
    const [filter, setFilter] = useState<FilterType>('all');
    const totals = getSalesTotals(MOCK_SALES);

    const filteredSales: Sale[] = filter === 'all'
        ? MOCK_SALES
        : MOCK_SALES.filter(s => s.type === filter);

    const filterOptions: { label: string; value: FilterType }[] = [
        { label: 'Tutte', value: 'all' },
        { label: 'Scheda', value: SaleType.Scheda },
        { label: 'Physical Coaching', value: SaleType.PhysicalCoaching },
    ];

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            {/* Page Header */}
            <div
                className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-4 anim-slide-up'
                style={{
                    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className='bi bi-cart-fill' style={{ fontSize: '1.4rem', color: '#ff4949' }}></i>
                        <h4 className='mb-0 fw-bold' style={{ color: '#333' }}>Vendite</h4>
                        <span className='badge rounded-pill' style={{ backgroundColor: '#ff4949', color: '#fff', fontSize: '0.8rem' }}>
                            {totals.count}
                        </span>
                    </div>
                    <p className='mb-0 mt-1' style={{ color: '#666', fontSize: '0.9rem' }}>
                        Gestisci le vendite di schede e coaching
                    </p>
                </div>
            </div>

            {/* Summary Pills */}
            <div className='d-flex flex-wrap gap-2 mb-4 anim-slide-up'>
                {statPill('Ricavo totale', `\u20AC${totals.revenue.toFixed(2)}`)}
                {statPill('Schede', `${totals.schedaCount} (\u20AC${totals.schedaRevenue.toFixed(2)})`)}
                {statPill('Coaching', `${totals.coachingCount} (\u20AC${totals.coachingRevenue.toFixed(2)})`)}
                {statPill('Pagati', totals.paidCount)}
                {statPill('In attesa', totals.pendingCount)}
            </div>

            {/* Filter Row */}
            <div className='d-flex flex-wrap gap-2 mb-4 anim-slide-up'>
                {filterOptions.map(opt => {
                    const isActive = filter === opt.value;
                    return (
                        <button
                            key={opt.value}
                            onClick={() => setFilter(opt.value)}
                            style={{
                                padding: '6px 16px',
                                borderRadius: 50,
                                border: isActive ? '1.5px solid #ff4949' : '1.5px solid #e0e0e0',
                                backgroundColor: isActive ? '#ff4949' : '#fff',
                                color: isActive ? '#fff' : '#666',
                                fontSize: '0.82rem',
                                fontWeight: isActive ? 600 : 500,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>

            {/* Sales List */}
            <div className='d-flex flex-column gap-3 mb-4'>
                {filteredSales.map((sale, index) => (
                    <div
                        key={sale.id}
                        className='app-card anim-stagger'
                        style={{
                            '--anim-index': index,
                            borderLeft: '3px solid #ff4949',
                            padding: '16px 20px',
                        } as React.CSSProperties}
                    >
                        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2'>
                            {/* Left */}
                            <div className='d-flex flex-column gap-1' style={{ flex: 1, minWidth: 0 }}>
                                <div className='d-flex align-items-center gap-2 flex-wrap'>
                                    <span
                                        className='badge rounded-pill'
                                        style={{
                                            backgroundColor: sale.type === SaleType.Scheda ? '#4a8fd920' : '#7b68c920',
                                            color: sale.type === SaleType.Scheda ? '#4a8fd9' : '#7b68c9',
                                            fontSize: '0.72rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {sale.type}
                                    </span>
                                    <span style={{ fontWeight: 700, color: '#222', fontSize: '0.95rem' }}>
                                        {sale.customerName}
                                    </span>
                                </div>
                                <span style={{ color: '#aaa', fontSize: '0.78rem' }}>
                                    {moment(sale.date).format('D MMMM YYYY')}
                                </span>
                                {sale.notes && (
                                    <span style={{ color: '#999', fontSize: '0.75rem', marginTop: 2 }}>
                                        {sale.notes}
                                    </span>
                                )}
                            </div>

                            {/* Right */}
                            <div className='d-flex align-items-center gap-3 flex-shrink-0'>
                                <span style={{ fontWeight: 700, color: '#222', fontSize: '1.1rem' }}>
                                    {'\u20AC'}{sale.amount.toFixed(2)}
                                </span>
                                <span
                                    className='badge rounded-pill'
                                    style={{
                                        backgroundColor: sale.paymentStatus === PaymentStatus.Paid ? '#1a7f4218' : '#b0860018',
                                        color: sale.paymentStatus === PaymentStatus.Paid ? '#1a7f42' : '#b08600',
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {sale.paymentStatus}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </PageTransition>
    );
};
